import { useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import "../assets/scss/components/_addon-section.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Moet from "../assets/images/addons/moet.jpg";
import Config from "../Config";
import { useViewportWidth } from "../hooks/useViewportWidth";
import { useProductStore } from "../stores/ProductStore";
import { useUserStore } from "../stores/UserStore";
import type { AddonSectionProps } from "../types/components/AddonSectionProps";
import { Image } from "./Image";
import { Slider } from "./Slider";

export const AddonSection = ({ addedToCart }: AddonSectionProps) => {
	const { userInfo } = useUserStore((state) => state);
	const { liveUrl } = Config;
	const { availableAddons, SetAddons, selectedAddons, includedAddons, addonSort } = useProductStore(({ availableAddons, SetAddons, selectedAddons, includedAddons, addonSort }) => ({
		availableAddons,
		SetAddons,
		selectedAddons,
		includedAddons,
		addonSort,
	}));
	const viewportWidth = useViewportWidth();

	const sortedAddons = Object.keys(availableAddons).sort((a, b) => {
		return addonSort.indexOf(parseInt(a)) - addonSort.indexOf(parseInt(b));
	});

	const classification = {
		Hampers: ["gift"],
		Alcohol: ["37.5cl", "75cl", "champagne", "prosecco", "wine", "gin", "rum", "tequila", "vodka", "whiskey"],
		Food: ["chocolates", "sweets", "biscuits", "cakes", "guylian", "jellies", "chocs", "chocolate", "cake"],
		Vases: ["vase"],
		Other: [],
	};

	const getClassification = (addon) => {
		for (const [classificationLabel, keywords] of Object.entries(classification)) {
			for (const keyword of keywords) {
				if (addon.includes(keyword)) {
					return classificationLabel;
				}
			}
		}
		return "Other";
	};

	const classifiedAddons = Object.keys(availableAddons).reduce((acc, addonId) => {
		const addon = availableAddons[addonId];
		const classification = getClassification(addon.pretty_name.toLowerCase());
		if (!acc[classification]) {
			acc[classification] = [];
		}
		acc[classification].push(addonId);
		return acc;
	}, {});

	const addonsPerSlide = viewportWidth > 992 ? 10 : viewportWidth > 768 ? 6 : 4;

	const addonSlides = sortedAddons.reduce(
		(acc, addonId) => {
			if (includedAddons[addonId] !== undefined) return acc;
			// Check if a new slide should be started
			if (acc[acc.length - 1].length === addonsPerSlide) {
				acc.push([]);
			}

			acc[acc.length - 1].push(addonId);

			return acc;
		},
		[[]],
	); // Initialize with one empty array to handle the first slide

	const classifiedSlides = Object.keys(classifiedAddons).reduce((acc, classification) => {
		//return object of slides for each classification
		const addons = classifiedAddons[classification];

		const slides = addons.reduce(
			(acc, addonId) => {
				if (includedAddons[addonId] !== undefined) return acc;
				// Check if a new slide should be started
				if (acc[acc.length - 1].length === addonsPerSlide) {
					acc.push([]);
				}

				acc[acc.length - 1].push(addonId);
				return acc;
			},
			[[]],
		);

		acc[classification] = slides;
		return acc;
	}, {});

	const showTooltip = (event, addonId) => {
		event.stopPropagation();
		const tooltipElement = document.querySelector(`[data-addon-id="${addonId}"] .addon-tooltip-text`);

		if (tooltipElement) {
			tooltipElement.classList.toggle("active");

			document.addEventListener("click", () => {
				tooltipElement.classList.remove("active");
			});
		}
	};

	const handleClassificationClick = (classification) => {
		setClassificationOpen((prev) => (prev === classification ? null : classification));
	};

	const [classificationOpen, setClassificationOpen] = useState(null);

	const activeSlides = classificationOpen ? classifiedSlides[classificationOpen] : addonSlides;

	return availableAddons && includedAddons && Object.keys(availableAddons).length > 0 ? (
		<section className="addon-section">
			<strong className="section-title">Let's make your gift even more special:</strong>

			{includedAddons && Object.keys(includedAddons).length > 0 && (
				<div
					className="included-addons-wrapper row"
					style={{
						"--gap": "10px",
					}}
				>
					<strong className="col-12">Included With This Product:</strong>
					{Object.keys(includedAddons).map((addonId) => {
						let addon = includedAddons[addonId];
						addonId = parseInt(addonId);

						return (
							<div
								className="addon included-addon col-12 col-md-4"
								key={addonId}
							>
								<div className="row align-items-center justify-content-space-between">
									<div className="addon-image col-3">
										<Image
											src={`${liveUrl}/images/attributes/${includedAddons[addonId].image_name}`}
											alt={addon.pretty_name}
											size="200"
										/>
									</div>
									<div className="addon-info row align-items-start justify-content-space-between col-8">
										<span className="addon-name col-fluid">{addon.pretty_name}</span>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<div className="addon-classification-wrapper">
				<span className="tooltip">Show me:</span>
				{classifiedAddons &&
					Object.keys(classifiedAddons).length > 0 &&
					Object.keys(classifiedAddons).map((classification) => {
						if (classifiedAddons[classification].length === 0) return null;
						return (
							<div
								key={classification}
								className={`addon-classification addon-classification-${classification} ${classificationOpen === classification ? "active" : ""}`}
								onClick={() => {
									handleClassificationClick(classification);
								}}
							>
								<strong className="classification-title">{classification}</strong>
							</div>
						);
					})}
			</div>

			<Slider
				slides={activeSlides.map((slide, index) => {
					return {
						element: (
							<div
								className="addons-wrapper row justify-content-flex-start"
								style={{
									"--gap": "10px",
								}}
							>
								{slide.map((addonId) => {
									let addon = availableAddons[addonId];
									addonId = parseInt(addonId);

									return (
										<div
											className="addon col-6 col-md-5th"
											key={addonId}
											onClick={() => {
												SetAddons(addonId);
											}}
											data-addon-id={addonId}
										>
											<div className="addon-image">
												{addon.tooltip ? (
													<button
														className="addon-tooltip"
														onClick={(event) => showTooltip(event, addonId)}
													>
														<FontAwesomeIcon icon={faInfoCircle} />
														<div
															className="addon-tooltip-text"
															data-addon
														>
															<p>{addon.tooltip}</p>
														</div>
													</button>
												) : (
													""
												)}

												<Image
													src={`${liveUrl}/images/attributes/${availableAddons[addonId].image_name}`}
													alt={addon.pretty_name}
													width="200"
												/>

												<div className={`addon-check-status ${selectedAddons.includes(addonId) ? "checked" : ""}`}>{selectedAddons.includes(addonId) ? "Added To Cart" : "Add To Cart"}</div>

												{userInfo.is_staff === "Yes" ? <div className="addon-id">{addonId}</div> : ""}
											</div>
											<div className="addon-info row align-items-start justify-content-space-between">
												<span className="addon-name col-fluid">{addon.pretty_name}</span>
												<p className="addon-price">{parseFloat(addon.price) === 0 ? <strong>FREE!</strong> : `£${parseFloat(addon.price).toFixed(2)}`}</p>
											</div>
										</div>
									);
								})}
							</div>
						),
					};
				})}
				slides_per_row={{
					base: 1,
					md: 1,
				}}
				prefix="addons-slider"
				autoplay={false}
				arrows={true}
			/>
		</section>
	) : (
		<></>
	);
};
